'use client'

import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { useUserQuery } from 'auth/hooks'
import { formatUserName } from 'utils/format-user-name'
import { IconShield, IconEast } from '@repo/design-system-cf/components/icons'
import { Tooltip } from 'primitives/tooltip'
import { Button } from '@repo/design-system-cf/components/button'

import { Spinner } from '@repo/design-system-cf/components/spinner'
import { useClubhousesListCommunityLeadersQuery } from '@web/gen/clubhouses'
import { FollowButton } from '../members/_components/follow-button'

export function LeadersBox({ slug }: { slug: string }) {
  const t = useTranslations('pages.clubhouse')
  const { data: user } = useUserQuery()
  const { data: leadersData } = useClubhousesListCommunityLeadersQuery(
    {
      input: {
        clubhouseSlug: slug,
        page: 1,
        itemsPerPage: 3,
      },
    },
    {
      select: data => data.clubhousesListCommunityLeaders.leaders,
    },
  )

  const leadersCount = leadersData?.length || ''

  return (
    <div className="sm:w-1/2 xl:w-2/5">
      <div
        className="bg-cf-white relative h-full w-full flex-col gap-2 rounded-[12px] border border-[#DADCE2] p-4 flex"
        data-testid="clubhouses-community-leaders-container"
      >
        <div>
          <p className="text-20 font-bold text-cf-grey-1000">
            {t('leadersbox.title')}
            {leadersCount ? (
              <span className="font-bold text-20 text-cf-grey-500 px-2">•</span>
            ) : null}
            <span className="font-bold text-20 text-cf-grey-500">{leadersCount}</span>
          </p>
          <div className="mt-2 flex w-full flex-col gap-4">
            {leadersData?.length ? (
              leadersData.map((leader, index) => {
                const isOwner = user?.me?.user?.id === leader.user.id
                return (
                  <div
                    className="flex h-auto flex-col lg:flex-row justify-between gap-4 w-full"
                    key={leader.user.id}
                  >
                    <div className="flex gap-4 items-center w-full">
                      <div>
                        <Link
                          href={`${AppRoutes.SOCIAL_FEED_CRAFTER}/${leader.user.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          key={leader.user.id}
                          className="size-16 block"
                        >
                          <Image
                            src={
                              leader.user?.avatarUrl ||
                              `https://avatar.iran.liara.run/public/${index}`
                            }
                            alt={leader.user?.displayName || 'User'}
                            width={64}
                            height={64}
                            className="overflow-hidden rounded-full border border-[#1313151F] object-cover size-16"
                          />
                        </Link>
                      </div>
                      <div className="flex flex-row flex-wrap items-center gap-2 w-full">
                        <div
                          className="flex flex-col gap-2 min-w-[200px] flex-1"
                          data-testid="clubhouses-community-leaders-user"
                        >
                          <Link
                            href={`${AppRoutes.SOCIAL_FEED_CRAFTER}/${leader.user.slug}`}
                            className="text-14 font-semibold"
                          >
                            {leader.user?.firstName} {leader.user?.lastName}
                          </Link>
                          <div>
                            <Tooltip
                              delay={0}
                              value={
                                <div className="relative max-w-[247px] py-2 bg-cf-white rounded-2 border border-[#0A0A0B1A] shadow-[0px_1px_3px_1px_rgba(0,0,0,0.1),_0px_1px_2px_0px_rgba(0,0,0,0.1)]">
                                  <div className="absolute left-1/2 -top-[6px] -translate-x-1/2 flex justify-center w-0 h-0">
                                    <div className=" w-0 h-0 border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent border-b-[6px] border-[#0A0A0B1A]" />
                                    <div className="absolute top-[2px] left-[-7px] w-0 h-0 border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent border-b-[6px] border-b-cf-white" />
                                  </div>
                                  <p className="text-cf-sf-gray-1100 text-12">
                                    {formatUserName(
                                      leader.user?.firstName,
                                      leader.user?.lastName,
                                      'User',
                                    )}
                                    {t('leadersbox.tooltip')}
                                  </p>
                                </div>
                              }
                              side="bottom"
                              className="z-50 bg-transparent p-0"
                              sideOffset={8}
                              hideArrow
                            >
                              <div className="inline-flex justify-center items-center gap-1 bg-cf-sky-blue-50 border border-[#1313151F] rounded-[24px] px-2">
                                <IconShield className="size-3.5 text-cf-sky-blue-800" />
                                <p className="text-cf-sky-blue-800 font-medium overflow-hidden inline-block max-w-fit text-ellipsis whitespace-nowrap truncate">
                                  {t('leadersbox.badge_text')}
                                </p>
                              </div>
                            </Tooltip>
                          </div>
                        </div>

                        <div className="flex w-full sm:w-auto justify-start sm:justify-end">
                          {!isOwner ? (
                            <FollowButton
                              size="small"
                              variant="outlined"
                              following={leader?.user?.following}
                              userId={leader?.user?.id}
                            />
                          ) : (
                            <div className="h-[26px]" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="py-4">
                <Spinner />
              </div>
            )}
          </div>
        </div>
        <div className="border-t border-[#1313151F] pt-4 pb-1 flex justify-center items-center mt-3">
          <Button variant="ghost" iconRight={<IconEast className="size-6" />}>
            <Link href={`${AppRoutes.CLUBHOUSE}${slug}/members/?member=community-leaders`}>
              {t('leadersbox.see_all', { count: leadersCount })}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  )
}
